import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../../Store/LoadingManager';

import QueryBuilderHelper from "../../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../../Utilities/Helpers/DropdownOptionsHelper";

import { CachingEnum } from '../../../Utilities/Enums/CachingEnum';

import { GetVendors, GetVendorById, CreateVendorService, EditVendorService, DeleteVendorService } from "../../../Api/Service/Maintenance/Vendors/vendorsService";

import { ErrorMessage, SuccessMessage } from "../../../Constants/Messages";
import { LegendItemInterface } from "../../../Models/Common/List/LegendInterface";
import { Vendor } from "../../../Models/Maintenance/Vendors/Vendor";
import { ActiveStatus, ActiveStatusEnum } from "../../../Utilities/Enums/StatusEnums";
import { VendorField } from "../../../Utilities/Enums/ParametersEnum";

export function useVendorsHook(q_queryKey: any, params: any, runInitialFetch: boolean = true) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const DropdownList = [CachingEnum.STATUS_LIST];
    let { loading, setLoading } = useLoadingScreen();

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getVendorsList(params),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const getVendorsList = async (params: any) => {
        let _data;
        let _legends;
        const responseData = await GetVendors(QueryBuilderHelper.BuildQueryString(params));
        
        if (responseData?.isSuccess) {
            _data = {
               ...responseData?.data,
               data: {
                ...responseData?.data?.data,
                list: responseData?.data?.data?.list?.map((item: Vendor) => { return { ...item, [VendorField.COMPANY_PUBLIC_IDS]: (item?.[VendorField.COMPANIES]?.map((company: any) => { return company.publicId }) ?? []) }}) ?? [],
               } 
            }
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        _legends = finalizeLegend(_data)
        return { ..._legends, data: _data?.data}
    }

    const finalizeLegend = (_data: any) => {
        const countDict = _data?.data?.metaData?.statusCount
        const totalCount = countDict?.find((x: any | null) => x?.status == '-1')?.count ?? 0
        const legends = ([
            {
                ...ActiveStatus[ActiveStatusEnum.ALL],
                count: totalCount
            },
            {
                ...ActiveStatus[ActiveStatusEnum.ACTIVE],
                count: countDict?.find((x: any | null) => x?.status == ActiveStatusEnum.ACTIVE)?.count ?? 0
            },
            {
                ...ActiveStatus[ActiveStatusEnum.INACTIVE],
                count: countDict?.find((x: any | null) => x?.status == ActiveStatusEnum.INACTIVE)?.count ?? 0
            },
        ])
        return { legends: legends, totalCount: totalCount}
    }

    const getVendorById = async (id: string | number) => {
        setLoading(true);
        
        const responseData = await GetVendorById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const createVendor = async (params: any) => {
        setLoading(true);
        const responseData = await CreateVendorService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Vendor " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const editVendorById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await EditVendorService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Vendor " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const deleteVendorById = async (id: any, remarks?: any) => {
        setLoading(true);

        const responseData = await DeleteVendorService(id, JSON.stringify({remarks: remarks ?? ""}));

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Vendor " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    return { data, DropdownData, fetchStatus, dropdownFetchStatus, getVendorById, createVendor, editVendorById, deleteVendorById }
}